import { Component, Prop, h, Host, Element } from '@stencil/core';

const COMPONENT_NAME = 'dh-section';

type GapValue = 'none' | '2xs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | '6xl' | '7xl';

/**
 * A responsive section wrapper with built-in container constraints and vertical spacing.
 *
 * Provides max-width centering, horizontal padding, and configurable vertical padding
 * across four breakpoints. Renders directly on the host element using the `<Host>` pattern
 * for minimal DOM nesting.
 *
 * Set `no-container` to disable max-width and horizontal padding for full-bleed layouts.
 * For semantic meaning, apply ARIA `role` attributes directly on the element
 * (e.g. `role="region"`, `role="banner"`).
 *
 * @category Elements
 *
 * @slot default - {required}, {multiple} - Section content (rows, headings, text blocks, etc.)
 *
 * @example
 *   <dh-section y-gap="4xl" y-gap-lg="6xl" role="region" aria-label="Features">
 *     <dh-row x-gap="lg" x-gap-lg="2xl">
 *       <dh-column span="12" span-sm="6">Left content</dh-column>
 *       <dh-column span="12" span-sm="6">Right content</dh-column>
 *     </dh-row>
 *   </dh-section>
 */
@Component({
  tag: 'dh-section',
  styleUrl: 'index.scss',
  shadow: false,
})
export class DhSection {
  @Element() host!: HTMLElement;

  // ── Vertical gap (padding-top / padding-bottom) ───────────────

  /**
   * Vertical padding on mobile (base breakpoint).
   * Maps to `--spacing-{value}` design token.
   * @default 5xl
   */
  @Prop() yGap: GapValue = '5xl';

  /**
   * Vertical padding on small screens (>=768px, tablet).
   */
  @Prop() yGapSm?: GapValue;

  /**
   * Vertical padding on medium screens (>=1024px, desktop).
   */
  @Prop() yGapMd?: GapValue;

  /**
   * Vertical padding on large screens (>=1280px, large desktop).
   * @default 7xl
   */
  @Prop() yGapLg: GapValue = '7xl';

  // ── Container behaviour ───────────────────────────────────────

  /**
   * If true, disables max-width constraint and horizontal padding.
   * Use for full-bleed backgrounds or edge-to-edge layouts.
   */
  @Prop() noContainer: boolean = false;

  private getMergedClasses(): string {
    const hostClass = (this.host.getAttribute('class') ?? '')
      .split(' ')
      .filter(c => c && !c.startsWith(COMPONENT_NAME))
      .join(' ');

    const classes = [
      COMPONENT_NAME,

      !this.noContainer ? `${COMPONENT_NAME}--container` : null,

      this.yGap && this.yGap !== 'none' ? `${COMPONENT_NAME}--y-gap-${this.yGap}` : null,
      this.yGapSm ? `${COMPONENT_NAME}--y-gap-sm-${this.yGapSm}` : null,
      this.yGapMd ? `${COMPONENT_NAME}--y-gap-md-${this.yGapMd}` : null,
      this.yGapLg && this.yGapLg !== 'none' ? `${COMPONENT_NAME}--y-gap-lg-${this.yGapLg}` : null,

      hostClass || null,
    ];

    return classes.filter(Boolean).join(' ').trim();
  }

  render() {
    return (
      <Host class={this.getMergedClasses()}>
        <slot />
      </Host>
    );
  }
}
